import { useCallback } from 'react';

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { useCampaignTracking } from 'src/app/shared/hooks/campaignTracking';
import { useLocale } from 'src/app/shared/hooks/l10n';

import { useCanReportVideo } from '../../../shared/hooks/useCanReportVideo';
import type { SupporterVideo } from '../../../shared/types';

type Result = ModelHookResult<
	{ canReportVideo: boolean },
	{
		handleReportClick: () => void;
	}
>;

export function useReportVideo({ petitionId, video }: { petitionId: string; video: SupporterVideo }): Result {
	const { track } = useCampaignTracking();
	const locale = useLocale();
	const allowedLocales = ['en-US', 'en-GB'];

	const { canReportVideo, currentUser } = useCanReportVideo(video);

	const handleReportClick = useCallback(() => {
		let payload: TrackingProperties = {
			petition_id: petitionId,
			reporter_id: currentUser?.id,
		};
		payload = video.videoUrl
			? { ...payload, video_id: video.id, video_creator_id: video.user?.id }
			: { ...payload, comment_id: video.id, comment_creator_id: video.user?.id };

		void track('supporter_voice_report_button_click', payload);
	}, [currentUser, petitionId, track, video]);

	return {
		data: { canReportVideo: canReportVideo && allowedLocales.includes(locale) },
		actions: {
			handleReportClick,
		},
	};
}
